import { useMemo } from 'react';
import type { components } from '@bookingdei/api-client/types';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { formatMoney } from '@bookingdei/i18n/format-money';
import { useLanguage } from '../../shell/language-context';
import { useSettings } from '../../shell/settings-context';
import { priceUnitLabel } from '../../shell/price-unit-label';

type Category = components['schemas']['Category'];
type BusinessSummary = components['schemas']['BusinessSummary'];


export interface HomeFormat {
  // The registry currency symbol (settings), for decorative glyphs.
  symbol: string;
  num: (n: number) => string;
  // Ratings always carry one decimal ("4.0", "৪.৭").
  rating: (n: number) => string;
  money: (minor: number) => string;
  unit: (u: string | undefined) => string;
  // "From ৳8,500/night", or "Free reservation" for a listing with no price.
  priceFrom: (b: BusinessSummary) => string;
  catLabel: (id: string | undefined) => string;
}

// Every number and amount on the home page goes through the packages/i18n formatters
// (CB-15): grouped 12,34,567 and Bangla digits under bn. Currency comes from the
// settings registry, never a literal (INF-1).
export function useHomeFormat(categories: Category[]): HomeFormat {
  const { t, lang } = useLanguage();
  const { settings } = useSettings();
  const symbol = settings?.currency?.symbol ?? settings?.currency?.code ?? '';
  const scale = settings?.currency?.minor_unit_scale ?? 100;

  return useMemo(() => {
    const byId = new Map(categories.map((c) => [c.id, c]));
    const num = (n: number) => formatNumber(lang, n);
    const rating = (n: number) => formatNumber(lang, n, { minFractionDigits: 1, maxFractionDigits: 1 });
    const money = (minor: number) => formatMoney(lang, minor, { currencySymbol: symbol, minorUnitScale: scale });
    const unit = (u: string | undefined) => priceUnitLabel(t, u);
    const catLabel = (id: string | undefined) => {
      if (!id) return '';
      const c = byId.get(id);
      if (!c) return id;
      return (lang === 'bn' ? c.label_bn : c.label_en) ?? c.label_en;
    };
    const priceFrom = (b: BusinessSummary) =>
      b.price_from && b.price_from > 0
        ? t('dc50.card.price_from', { price: money(b.price_from) + unit(b.price_unit) })
        : t('dc50.card.free_reservation');
    return { symbol, num, rating, money, unit, priceFrom, catLabel };
  }, [categories, lang, symbol, scale, t]);
}
